import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, Modal, StyleSheet } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface TitleEditModalProps {
    visible: boolean;
    onClose: () => void;
    currentTitle: string;
    onSave: (newTitle: string) => void;
    sessionTitle?: string;
}

const TitleEditModal: React.FC<TitleEditModalProps> = ({
    visible,
    onClose,
    currentTitle,
    onSave,
    sessionTitle = "Edit Session Title"
}) => {
    const [title, setTitle] = useState(currentTitle || '');
    const [error, setError] = useState('');

    // Update state when modal opens or currentTitle changes
    useEffect(() => {
        if (visible) {
            setTitle(currentTitle || '');
            setError('');
        }
    }, [visible, currentTitle]);

    const validateTitle = (value: string) => {
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return 'Session title is required';
        }
        if (trimmedValue.length < 3) {
            return 'Title must be at least 3 characters long';
        }
        if (trimmedValue.length > 100) {
            return 'Title cannot exceed 100 characters';
        }
        return '';
    };

    const handleTitleChange = (text: string) => {
        setTitle(text);
        setError(validateTitle(text));
    };

    const isValid = !error && title.trim() !== '';

    const handleSave = () => {
        const validationError = validateTitle(title);
        if (validationError) {
            setError(validationError);
            return;
        }
        onSave(title.trim());
        onClose();
    };

    const handleCancel = () => {
        setTitle(currentTitle || '');
        onClose();
    };

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={styles.overlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.title}>{title || sessionTitle}</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={20} color="#666" />
                        </Pressable>
                    </View>

                    {/* Content */}
                    <View style={styles.content}>
                        <Text style={styles.label}>Session Title</Text>
                        <TextInput
                            style={[styles.input, error ? styles.inputError : null]}
                            value={title}
                            onChangeText={handleTitleChange}
                            placeholder="Enter session title (e.g., Beginner Swimming)"
                            placeholderTextColor="#adb5bd"
                            autoFocus={true}
                            selectTextOnFocus={true}
                            multiline={false}
                            maxLength={100}
                        />
                        
                        <View style={styles.characterCount}>
                            <Text style={styles.characterCountText}>{title.length}/100</Text>
                        </View>
                        
                        {error ? (
                            <Text style={styles.errorText}>{error}</Text>
                        ) : (
                            <Text style={styles.helperText}>
                                Give your session a descriptive and engaging title
                            </Text>
                        )}
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable onPress={handleCancel} style={[styles.button, styles.cancelButton]}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable 
                            onPress={handleSave} 
                            style={[styles.button, isValid ? styles.saveButton : styles.saveButtonDisabled]}
                            disabled={!isValid}
                        >
                            <Text style={[styles.saveButtonText, !isValid && styles.saveButtonTextDisabled]}>Save</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        width: '100%',
        maxWidth: 400,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
        elevation: 8,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
        flex: 1,
        marginRight: 12,
    },
    closeButton: {
        padding: 4,
    },
    content: {
        padding: 20,
    },
    label: {
        fontSize: 16,
        fontWeight: '500',
        color: '#333',
        marginBottom: 8,
    },
    input: {
        borderWidth: 1,
        borderColor: '#dee2e6',
        borderRadius: 12,
        padding: 16,
        fontSize: 16,
        backgroundColor: '#ffffff',
        minHeight: 52,
        color: '#212529',
    },
    inputError: {
        borderColor: '#dc3545',
        backgroundColor: '#fff5f5',
    },
    characterCount: {
        alignItems: 'flex-end',
        marginTop: 4,
    },
    characterCountText: {
        fontSize: 11,
        color: '#999',
    },
    helperText: {
        fontSize: 12,
        color: '#666',
        marginTop: 8,
        textAlign: 'center',
    },
    errorText: {
        fontSize: 12,
        color: '#dc3545',
        marginTop: 8,
        textAlign: 'center',
        fontWeight: '500',
    },
    footer: {
        flexDirection: 'row',
        padding: 20,
        paddingTop: 0,
        gap: 12,
    },
    button: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 8,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    cancelButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: '#666',
    },
    saveButton: {
        backgroundColor: '#007bff',
    },
    saveButtonDisabled: {
        backgroundColor: '#cccccc',
    },
    saveButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: 'white',
    },
    saveButtonTextDisabled: {
        color: '#666666',
    },
});

export default TitleEditModal; 